import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useSelector, useDispatch } from 'react-redux'
import tcPopin from 'src/models/tcPopin/tcPopin'

import { ObjectivesSteps } from '../models/Objectives'
import { UserState } from '../redux/user/redux'
import { selectors, actions } from '../redux'
import {
  planningWelcomeBackModalTransformer,
  planningNewObjectivesModalTransformer,
} from '../models/Planning'
import { ActionButtonProps } from '../components/ActionButton'
import routes from '../routes/definitions'

type usePlanningModalsParams = {
  forceWelcomeBackModal?: boolean
  isLastDay?: boolean
}

const usePlanningModals = (params?: usePlanningModalsParams) => {
  const { t } = useTranslation()
  const nextRouter = useRouter()
  const dispatch = useDispatch()

  const forceWelcomeBackModal =
    (typeof params === 'object' && params?.forceWelcomeBackModal) ?? false

  const [isWelcomeBackModalOpen, setIsWelcomeBackModalOpen] = useState(
    (forceWelcomeBackModal || params?.isLastDay) ?? false
  )
  useEffect(() => {
    if (params?.isLastDay) {
      setIsWelcomeBackModalOpen(true)
    }
  }, [params?.isLastDay])
  const [isNewObjectivesModalOpen, setIsNewObjectivesModalOpen] = useState(
    false
  )
  const [newObjectiveIndex, setNewObjectiveIndex] = useState(0)
  const nutritionalPreferences: UserState['nutritionalPreferences'] = useSelector(
    selectors.user.nutritionalPreferences
  )
  const nutriSteps = ObjectivesSteps(t, nutritionalPreferences, undefined, true)
  const canImpairNutritionalPreferences: boolean = useSelector(
    selectors.user.canImpairNutritionalPreferences(nutriSteps)
  )

  const canImproveNutritionalPreferences: boolean = useSelector(
    selectors.user.canImproveNutritionalPreferences(nutriSteps)
  )

  const improvedObjectivesList: any = useSelector(
    selectors.user.improvedObjectivesList(nutriSteps)
  )

  const handleCloseWelcomeBackModal = useCallback(() => {
    setIsWelcomeBackModalOpen(false)
    dispatch(actions.user.generatePlanning())
  }, [setIsWelcomeBackModalOpen, dispatch])

  const handleCloseNewObjectivesModal = useCallback(() => {
    setIsNewObjectivesModalOpen(false)
    dispatch(actions.user.generatePlanning())
  }, [dispatch, setIsNewObjectivesModalOpen])

  const handleImproveClick = useCallback(() => {
    setIsWelcomeBackModalOpen(false)
    setIsNewObjectivesModalOpen(true)
  }, [setIsWelcomeBackModalOpen, setIsNewObjectivesModalOpen])

  const handleAnotherNewObjectives = useCallback(() => {
    setNewObjectiveIndex((prev) => prev + 1)
  }, [setNewObjectiveIndex])

  const handleAcceptNewObjectives = useCallback(() => {
    const newObjective = {
      [improvedObjectivesList[newObjectiveIndex]?.name ?? '']:
        improvedObjectivesList[newObjectiveIndex]?.improvedObjective?.id ?? '',
    }
    dispatch(actions.user.setNutritionalPreferences(newObjective))
    dispatch(
      actions.user.updateObjectivesRequest({
        params: newObjective,
      })
    )
    setIsNewObjectivesModalOpen(false)
  }, [
    dispatch,
    improvedObjectivesList,
    newObjectiveIndex,
    setIsNewObjectivesModalOpen,
  ])

  const welcomeBackButtons = useMemo(() => {
    return [
      ...(canImpairNutritionalPreferences
        ? [
            {
              text: t('planning_welcomeback_modal_impair_cta'),
              variantType: 'button',
              actionProps: {
                onClick: () => {
                  tcPopin({
                    popin_name: `popin maj objectifs_${t(
                      'planning_welcomeback_modal_impair_cta'
                    )}_mb`,
                  })
                  nextRouter.push(routes.objectives.path)
                },
              },
            } as ActionButtonProps,
          ]
        : []),
      {
        text: t('planning_welcomeback_modal_keep_cta'),
        variantType: 'button',
        variantColorScheme: !canImproveNutritionalPreferences
          ? 'black'
          : 'white',
        actionProps: {
          onClick: () => {
            tcPopin({
              popin_name: `popin maj objectifs_${t(
                'planning_welcomeback_modal_keep_cta'
              )}_mb`,
            })
            handleCloseWelcomeBackModal()
          },
        },
      } as ActionButtonProps,
      ...(canImproveNutritionalPreferences
        ? [
            {
              text: t('planning_welcomeback_modal_improve_cta'),
              variantType: 'button',
              variantColorScheme: 'black',
              actionProps: {
                onClick: () => {
                  tcPopin({
                    popin_name: `popin maj objectifs_${t(
                      'planning_welcomeback_modal_improve_cta'
                    )}_mb`,
                  })
                  handleImproveClick()
                },
              },
            } as ActionButtonProps,
          ]
        : []),
    ]
  }, [
    t,
    canImpairNutritionalPreferences,
    canImproveNutritionalPreferences,
    handleCloseWelcomeBackModal,
    handleImproveClick,
    nextRouter,
  ])

  const newObjectivesButtons = useMemo(
    () => [
      ...(newObjectiveIndex !== improvedObjectivesList?.length - 1
        ? [
            {
              text: t('planning_newobjectives_modal_another_cta'),
              variantType: 'button',
              actionProps: {
                onClick: () => {
                  tcPopin({
                    popin_name: `popin maj objectifs_${t(
                      'planning_newobjectives_modal_another_cta'
                    )}_mb`,
                  })
                  handleAnotherNewObjectives()
                },
              },
            } as ActionButtonProps,
          ]
        : []),
      {
        text: t('planning_newobjectives_modal_accept_cta'),
        variantType: 'button',
        variantColorScheme: 'black',
        actionProps: {
          onClick: () => {
            tcPopin({
              popin_name: `popin maj objectifs_${t(
                'planning_newobjectives_modal_accept_cta'
              )}_mb`,
            })
            handleAcceptNewObjectives()
          },
        },
      } as ActionButtonProps,
    ],
    [
      t,
      handleAnotherNewObjectives,
      handleAcceptNewObjectives,
      newObjectiveIndex,
      improvedObjectivesList,
    ]
  )

  const welcomeBackModalProps = useMemo(
    () =>
      planningWelcomeBackModalTransformer(
        t,
        isWelcomeBackModalOpen,
        handleCloseWelcomeBackModal,
        welcomeBackButtons
      ),
    [t, isWelcomeBackModalOpen, handleCloseWelcomeBackModal, welcomeBackButtons]
  )

  const newObjectivesModalProps = useMemo(
    () =>
      planningNewObjectivesModalTransformer(
        t,
        isNewObjectivesModalOpen,
        handleCloseNewObjectivesModal,
        newObjectivesButtons,
        improvedObjectivesList,
        newObjectiveIndex
      ),
    [
      t,
      isNewObjectivesModalOpen,
      handleCloseNewObjectivesModal,
      newObjectivesButtons,
      improvedObjectivesList,
      newObjectiveIndex,
    ]
  )

  return { welcomeBackModalProps, newObjectivesModalProps }
}
export default usePlanningModals
